import { Link, useNavigate } from 'react-router-dom';
import { t } from '@app/i18n';
import { Badge, Button } from '@zarya/ui';
import { HopTrail } from './HopTrail';
import {
  swapErrorBody,
  swapExecutor,
  swapResponse,
  swapStatus,
  swapDeuRight,
  type DecisionState,
  type SwapInScreen,
} from './ConverseModel';
import s from './Conversar.module.css';

export interface SwapProps {
  troca: SwapInScreen;
  /** Estado da decisão desta chamada — do evento do canal ou do polling REST. */
  decisao: DecisionState;
  /** Leva ao Simulador já preenchido — nulo quando não há identidade resolvida. */
  identityIdParaSimulador: string | null;
}

/**
 * Uma troca da conversa: a bolha do pedido, a do agente, a trilha do salto e a
 * linha da decisão do PDP.
 *
 * O componente lê `TrocaNaTela` — o modelo que os **dois** transportes produzem —
 * e não o resultado do REST. Pelo canal a troca chega em pedaços (os saltos
 * primeiro, a resposta depois), então cada parte é desenhada assim que existe e
 * a bolha do agente diz «chamando…» enquanto a resposta não veio. Ler o resultado
 * inteiro obrigaria a esperar tudo para desenhar qualquer coisa — que é
 * exatamente o que o canal existe para não fazer.
 */
export function Swap({ troca, decisao, identityIdParaSimulador }: SwapProps) {
  const navegar = useNavigate();
  const respondeu = troca.resposta !== null;
  const deuRight = swapDeuRight(troca);
  const erro = respondeu && !deuRight ? swapErrorBody(troca) : null;
  const executor = swapExecutor(troca);
  const alvo = troca.delegadoA ? `${troca.agente} → ${troca.delegadoA}` : troca.agente;
  const situacao = swapStatus(troca);

  // Enquanto a resposta não chegou, a bolha é neutra: pintá-la de vermelho por
  // `status_http` ausente diria «falhou» sobre uma chamada que ainda está em voo.
  //
  // E uma negação tem tom próprio (âmbar), como os saltos: ela é o produto
  // funcionando, e o vermelho de falha dizia o contrário na hora exata em que a
  // demonstração dá certo.
  const responseTom = !respondeu
    ? s.respostaEmVoo
    : deuRight
      ? s.respostaOk
      : situacao === 'negado'
        ? s.respostaNegada
        : s.respostaErro;

  return (
    <article className={s.troca}>
      <div className={`${s.bolha} ${s.pedido}`}>
        <span className={s.quem}>
          {troca.pessoa} → {alvo}
        </span>
        <span className={s.mono}>{troca.tool}</span>
      </div>

      <div className={`${s.bolha} ${s.resposta} ${responseTom}`}>
        <span className={s.quem}>{executor}</span>
        <span>{swapResponse(troca)}</span>
        {/* O código e o status ficam em segundo plano: são o detalhe técnico da
            recusa, não a frase que a demonstração precisa que se leia. */}
        {respondeu && !deuRight ? (
          <span className={s.detalheDaRecusa}>
            {erro?.erro ? <code className={s.mono}>{erro.erro}</code> : null}
            {erro?.erro ? ' · ' : null}
            <span className={s.mono}>HTTP {troca.resposta?.statusHttp}</span>
          </span>
        ) : null}

        {/* As duas listas do cruzamento do Rego: é o que transforma «negado» em
            «negado porque». Só aparecem quando o executor as mandou. */}
        {erro?.workload_pode || erro?.perfil_pode ? (
          <dl className={s.listas}>
            {erro.workload_pode ? (
              <div>
                <dt>O contrato de {executor} concede:</dt>
                <dd className={s.mono}>{erro.workload_pode.join(', ') || '(nada)'}</dd>
              </div>
            ) : null}
            {erro.perfil_pode ? (
              <div>
                <dt>O perfil de {troca.pessoa} carrega:</dt>
                <dd className={s.mono}>{erro.perfil_pode.join(', ') || '(nada)'}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {erro?.pode_invocar ? (
          <p className={s.nota}>
            «Pode invocar» de {troca.agente}:{' '}
            <span className={s.mono}>{erro.pode_invocar.join(', ') || '(nenhum)'}</span>
          </p>
        ) : null}

        {erro?.tools_do_agente ? (
          <p className={s.nota}>
            Tools que {executor} declara:{' '}
            <span className={s.mono}>{erro.tools_do_agente.join(', ') || '(nenhuma)'}</span>
          </p>
        ) : null}

        <HopTrail
          saltos={troca.saltos}
          delegacao={Boolean(troca.delegadoA)}
          // A situação das linhas derivadas (`a2a` e `decisao`) sai daqui quando o
          // executor não relata o salto — que é o caso do laboratório.
          desfecho={situacao}
          delegadoA={troca.delegadoA}
          agente={troca.agente}
          recusa={erro ?? undefined}
          tool={troca.tool}
        />

        <p className={s.decisao}>
          <DecisionRow decisao={decisao} />
        </p>

        {troca.resposta ? (
          <div className={s.rodape}>
            <span className={s.mono}>
              {troca.resposta.duracaoMs.toFixed(0)} ms · {troca.resposta.requestId}
            </span>
            {identityIdParaSimulador ? (
              <Button
                variant="out"
                size="sm"
                icon="play"
                // Como em T17: identidade e tool vão por **estado de rota**, não
                // na URL — o simulador não carrega dado de uma chamada na barra
                // de endereços.
                onClick={() =>
                  navegar('/simulador', {
                    state: { identityId: identityIdParaSimulador, tool: troca.tool },
                  })
                }
              >
                Reproduzir no simulador
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}

/**
 * A linha «Decisão do PDP» — o **registro** da decisão, não o veredito da chamada.
 *
 * Ela e a linha `decisao` da trilha contam coisas diferentes, e as duas ficam: a
 * da trilha diz o que aconteceu com esta chamada, agora; esta diz quando o
 * registro dela vai poder ser aberto em Decisões. O veredito do log nasce no
 * gateway e atravessa o Log Analytics, com 5 a 10 min de atraso no lab —
 * carimbar o `status_http` como se fosse ele seria quase sempre certo e
 * ocasionalmente falso, e é essa diferença que a tela existe para mostrar.
 *
 * Enquanto o registro não chega, a linha é **neutra** (decisão de 30/08, demo):
 * ela informa um prazo, e não um problema. Um DENY encontrado vai de âmbar, como
 * a marca `⊘` da trilha e a bolha da recusa — o vermelho é de coisa quebrada, e
 * uma negação do PDP é o produto trabalhando.
 */
function DecisionRow({ decisao }: { decisao: DecisionState }) {
  if (decisao.estado === 'encontrada') {
    const permitido = decisao.veredito === 'permitido';
    return (
      <>
        <span>{t('conversation.pdp.label')}</span>
        <Badge tone={permitido ? 'success' : 'warning'}>{permitido ? 'ALLOW' : 'DENY'}</Badge>
        <Link to="/decisoes" state={{ requestId: decisao.requestId }}>
          {t('conversation.pdp.open')}
        </Link>
      </>
    );
  }
  if (decisao.estado === 'nao_encontrada') {
    return (
      // O link fica no meio da frase: as duas metades são chaves porque o `t()`
      // devolve texto, não nó — o mesmo par `before`/`after` do resto do lote.
      <span>
        {t('conversation.pdp.notFound.before')}{' '}
        <Link to="/decisoes">{t('shell.nav.decisions')}</Link>{' '}
        {t('conversation.pdp.notFound.after')}
      </span>
    );
  }
  return <span>{t('conversation.pdp.waiting')}</span>;
}
